import { useState, useEffect } from "react";
import { supabase } from "./supabaseclient";

const T = {
  bg: "#06090f", panel: "#0b0f1a", card: "#0f1623", border: "#1a2540",
  gold: "#c9a96e", goldLight: "#e8c98a", goldDim: "#7a6640",
  text: "#e8eaf0", textMid: "#7a8599", textDim: "#3d4a5c",
  active: "#10b981", activeBg: "#071f14", activeText: "#34d399",
  completedBg: "#13132e", completedText: "#a5b4fc", moderate: "#fbbf24",
};

function FieldLabel({ children }) {
  return (
    <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.7px", textTransform: "uppercase", color: T.textDim, marginBottom: 5 }}>
      {children}
    </label>
  );
}

function getPainColor(score) {
  if (score >= 7) return "#f87171";
  if (score >= 4) return "#fbbf24";
  return "#34d399";
}

// ── SCALE CATEGORIES ──
const SCALE_CATEGORIES = {
  "Pain & Global": ["VAS", "NPRS", "PSFS", "GROC"],
  "Spine": ["OSWESTRY", "NDI", "START_BACK", "FABQ"],
  "Upper Limb": ["DASH", "ASES", "WOSI"],
  "Lower Limb": ["LEFS", "KOOS", "WOMAC", "IKDC", "LYSHOLM"],
  "Hip": ["HOOS"],
  "Tendinopathy": ["VISA_A", "VISA_P"],
  "Balance & Neuro": ["BERG", "TUG", "MAS"],
  "Psychological": ["ACL_RSI", "TAMPA", "OREBRO"],
};

const SCALES = {

  // ══════════════════════════════════════════
  // PAIN & GLOBAL MEASURES
  // ══════════════════════════════════════════

  VAS: {
    name: "VAS — Visual Analogue Scale",
    shortName: "VAS",
    category: "Pain & Global",
    description: "Patient rates pain from 0 (no pain) to 10 (worst imaginable pain).",
    mcid: "1.5 points",
    mdc: "2.0 points",
    timeToComplete: "1 min",
    items: 1,
    population: "All conditions",
    reference: "Huskisson EC. Pain. 1974;2(4):357–366.",
    maxScore: 10,
    questions: [
      { id: "vas", label: "Pain Score (0 = No pain, 10 = Worst imaginable)", type: "slider", min: 0, max: 10 },
    ],
    calculate: (answers) => ({ score: Number(answers.vas ?? 5), max: 10 }),
    interpret: (score) => {
      if (score <= 3) return { label: "Mild Pain", color: "#34d399" };
      if (score <= 6) return { label: "Moderate Pain", color: "#fbbf24" };
      return { label: "Severe Pain", color: "#f87171" };
    },
  },

  NPRS: {
    name: "NPRS — Numeric Pain Rating Scale",
    shortName: "NPRS",
    category: "Pain & Global",
    description: "11-point numerical scale (0–10). More reliable than VAS for verbal reporting. Recommended by IASP and most current clinical guidelines.",
    mcid: "2 points",
    mdc: "2 points",
    timeToComplete: "1 min",
    items: 3,
    population: "All conditions — universal",
    reference: "Farrar JT et al. Pain. 2001;94(2):149–158. DOI: 10.1016/S0304-3959(01)00349-9",
    maxScore: 10,
    questions: [
      { id: "current", label: "Current Pain (0 = No pain, 10 = Worst imaginable)", type: "slider", min: 0, max: 10 },
      { id: "worst", label: "Worst Pain in last 24 hours (0–10)", type: "slider", min: 0, max: 10 },
      { id: "best", label: "Best/Least Pain in last 24 hours (0–10)", type: "slider", min: 0, max: 10 },
    ],
    calculate: (answers) => {
      const current = Number(answers.current ?? 5);
      const worst = Number(answers.worst ?? 5);
      const best = Number(answers.best ?? 5);
      const avg = Math.round(((current + worst + best) / 3) * 10) / 10;
      return { score: avg, max: 10 };
    },
    interpret: (score) => {
      if (score <= 3) return { label: "Mild Pain", color: "#34d399" };
      if (score <= 6) return { label: "Moderate Pain", color: "#fbbf24" };
      return { label: "Severe Pain", color: "#f87171" };
    },
  },

  PSFS: {
    name: "PSFS — Patient Specific Functional Scale",
    shortName: "PSFS",
    category: "Pain & Global",
    description: "Patient identifies 3 important activities they cannot perform or have difficulty with. Rates each 0–10. Most responsive outcome measure for any musculoskeletal condition.",
    mcid: "2 points (average score)",
    mdc: "2 points",
    timeToComplete: "3 min",
    items: 3,
    population: "Any MSK condition — universally applicable",
    reference: "Stratford P et al. Physiother Can. 1995;47(4):258–263. DOI: 10.3138/ptc.47.4.258",
    maxScore: 10,
    questions: [
      { id: "activity1_name", label: "Activity 1 — Describe the activity (e.g. climbing stairs, running)", type: "text" },
      { id: "activity1_score", label: "Activity 1 — Ability score (0 = Unable, 10 = Same as before)", type: "slider", min: 0, max: 10 },
      { id: "activity2_name", label: "Activity 2 — Describe the activity", type: "text" },
      { id: "activity2_score", label: "Activity 2 — Ability score (0 = Unable, 10 = Same as before)", type: "slider", min: 0, max: 10 },
      { id: "activity3_name", label: "Activity 3 — Describe the activity", type: "text" },
      { id: "activity3_score", label: "Activity 3 — Ability score (0 = Unable, 10 = Same as before)", type: "slider", min: 0, max: 10 },
    ],
    calculate: (answers) => {
      const s1 = Number(answers.activity1_score ?? 0);
      const s2 = Number(answers.activity2_score ?? 0);
      const s3 = Number(answers.activity3_score ?? 0);
      const avg = Math.round(((s1 + s2 + s3) / 3) * 10) / 10;
      return { score: avg, max: 10 };
    },
    interpret: (score) => {
      if (score >= 7) return { label: "Minimal Limitation", color: "#34d399" };
      if (score >= 4) return { label: "Moderate Limitation", color: "#fbbf24" };
      return { label: "Severe Limitation", color: "#f87171" };
    },
  },

  GROC: {
    name: "GROC — Global Rating of Change Scale",
    shortName: "GROC",
    category: "Pain & Global",
    description: "Patient rates overall change since initial assessment. -7 (vastly worse) to +7 (completely recovered). Used as anchor in virtually all high-quality MSK RCTs.",
    mcid: "+3 to +5 (meaningful improvement)",
    mdc: "N/A",
    timeToComplete: "1 min",
    items: 1,
    population: "All conditions — universal anchor measure",
    reference: "Jaeschke R et al. J Clin Epidemiol. 1989;42(2):157–163. DOI: 10.1016/0895-4356(89)90005-8",
    maxScore: 7,
    questions: [
      {
        id: "groc",
        label: "Compared to when you first sought treatment, how would you describe your condition overall?",
        type: "radio",
        options: [
          "A great deal better (+7)",
          "A good deal better (+6)",
          "Quite a bit better (+5)",
          "Moderately better (+4)",
          "Somewhat better (+3)",
          "A little better (+2)",
          "A tiny bit better (+1)",
          "About the same (0)",
          "A tiny bit worse (-1)",
          "A little worse (-2)",
          "Somewhat worse (-3)",
          "Moderately worse (-4)",
          "Quite a bit worse (-5)",
          "A good deal worse (-6)",
          "A great deal worse (-7)",
        ],
      },
    ],
    calculate: (answers) => {
      const mapping = [7, 6, 5, 4, 3, 2, 1, 0, -1, -2, -3, -4, -5, -6, -7];
      const idx = Number(answers.groc ?? 7);
      return { score: mapping[idx] ?? 0, max: 7 };
    },
    interpret: (score) => {
      if (score >= 5) return { label: "Completely Recovered", color: "#34d399" };
      if (score >= 3) return { label: "Meaningfully Improved", color: "#86efac" };
      if (score >= 1) return { label: "Slightly Improved", color: "#fbbf24" };
      if (score === 0) return { label: "No Change", color: "#7a8599" };
      if (score >= -2) return { label: "Slightly Worse", color: "#fb923c" };
      return { label: "Significantly Worse", color: "#f87171" };
    },
  },

  // ══════════════════════════════════════════
  // SPINE MEASURES
  // ══════════════════════════════════════════

  OSWESTRY: {
    name: "Oswestry Disability Index",
    shortName: "ODI",
    category: "Spine",
    description: "Gold standard for lumbar spine disability. 10 sections, each scored 0–5. Score expressed as percentage.",
    mcid: "10 points",
    mdc: "6 points",
    timeToComplete: "5 min",
    items: 10,
    population: "Lumbar spine — LBP, disc herniation, stenosis",
    reference: "Fairbank JC, Pynsent PB. Spine. 2000;25(22):2940–2953. DOI: 10.1097/00007632-200011150-00017",
    maxScore: 100,
    questions: [
      { id: "q1", label: "1. Pain Intensity", type: "radio", options: ["I can tolerate the pain without medication (0)", "Pain is bad but I can manage without medication (1)", "Pain killers give complete relief (2)", "Pain killers give moderate relief (3)", "Pain killers give very little relief (4)", "Pain killers have no effect (5)"] },
      { id: "q2", label: "2. Personal Care (Washing, Dressing)", type: "radio", options: ["I can look after myself normally (0)", "I can look after myself but it is painful (1)", "It is painful and slow but I manage (2)", "I need some help but manage most of my personal care (3)", "I need help in most aspects of self care (4)", "I cannot get dressed, wash with difficulty, stay in bed (5)"] },
      { id: "q3", label: "3. Lifting", type: "radio", options: ["I can lift heavy weights without extra pain (0)", "I can lift heavy weights but with extra pain (1)", "Pain prevents me from lifting heavy weights off the floor (2)", "Pain prevents me lifting heavy weights but I manage light to medium (3)", "I can lift only very light weights (4)", "I cannot lift or carry anything (5)"] },
      { id: "q4", label: "4. Walking", type: "radio", options: ["Pain does not prevent me walking any distance (0)", "Pain prevents me walking more than 1 mile (1)", "Pain prevents me walking more than 1/2 mile (2)", "Pain prevents me walking more than 1/4 mile (3)", "I can only walk using a stick or crutches (4)", "I am in bed most of the time (5)"] },
      { id: "q5", label: "5. Sitting", type: "radio", options: ["I can sit in any chair as long as I like (0)", "I can only sit in my favourite chair as long as I like (1)", "Pain prevents me sitting more than 1 hour (2)", "Pain prevents me sitting more than 30 minutes (3)", "Pain prevents me sitting more than 10 minutes (4)", "Pain prevents me from sitting at all (5)"] },
      { id: "q6", label: "6. Standing", type: "radio", options: ["I can stand as long as I want without extra pain (0)", "I have extra pain when standing but it does not increase (1)", "Pain prevents me from standing for more than 1 hour (2)", "Pain prevents me from standing for more than 30 minutes (3)", "Pain prevents me from standing for more than 10 minutes (4)", "Pain prevents me from standing at all (5)"] },
      { id: "q7", label: "7. Sleeping", type: "radio", options: ["My sleep is never disturbed by pain (0)", "My sleep is occasionally disturbed by pain (1)", "Because of pain I have less than 6 hours sleep (2)", "Because of pain I have less than 4 hours sleep (3)", "Because of pain I have less than 2 hours sleep (4)", "Pain prevents me from sleeping at all (5)"] },
      { id: "q8", label: "8. Sex Life (if applicable)", type: "radio", options: ["My sex life is normal and causes no extra pain (0)", "My sex life is normal but causes some extra pain (1)", "My sex life is nearly normal but is very painful (2)", "My sex life is severely restricted by pain (3)", "My sex life is nearly absent because of pain (4)", "Pain prevents any sex life at all (5)"] },
      { id: "q9", label: "9. Social Life", type: "radio", options: ["My social life is normal and gives me no extra pain (0)", "My social life is normal but increases the degree of pain (1)", "Pain has no significant effect except limiting energetic interests (2)", "Pain has restricted my social life and I do not go out as often (3)", "Pain has restricted social life to my home (4)", "I have no social life because of pain (5)"] },
      { id: "q10", label: "10. Travelling", type: "radio", options: ["I can travel anywhere without pain (0)", "I can travel anywhere but it gives extra pain (1)", "Pain is bad but I manage journeys over 2 hours (2)", "Pain restricts me to journeys of less than 1 hour (3)", "Pain restricts me to short necessary journeys under 30 minutes (4)", "Pain prevents all travel except to receive treatment (5)"] },
    ],
    calculate: (answers) => {
      const total = ["q1","q2","q3","q4","q5","q6","q7","q8","q9","q10"].reduce((sum, id) => sum + Number(answers[id] || 0), 0);
      return { score: Math.round((total / 50) * 100), max: 100 };
    },
    interpret: (score) => {
      if (score <= 20) return { label: "Minimal Disability", color: "#34d399" };
      if (score <= 40) return { label: "Moderate Disability", color: "#fbbf24" };
      if (score <= 60) return { label: "Severe Disability", color: "#fb923c" };
      if (score <= 80) return { label: "Crippling Back Pain", color: "#f87171" };
      return { label: "Bed-Bound / Exaggerating", color: "#ef4444" };
    },
  },

  NDI: {
    name: "NDI — Neck Disability Index",
    shortName: "NDI",
    category: "Spine",
    description: "Gold standard for cervical spine disability. 10 sections, each scored 0–5. Score expressed as percentage. Sister scale to Oswestry for the cervical spine.",
    mcid: "7.5 points",
    mdc: "5 points",
    timeToComplete: "5 min",
    items: 10,
    population: "Cervical spine — neck pain, radiculopathy, whiplash",
    reference: "Vernon H, Mior S. J Manipulative Physiol Ther. 1991;14(7):409–415. PMID: 1834753",
    maxScore: 100,
    questions: [
      { id: "q1", label: "1. Pain Intensity", type: "radio", options: ["I have no pain at the moment (0)", "The pain is very mild at the moment (1)", "The pain is moderate at the moment (2)", "The pain is fairly severe at the moment (3)", "The pain is very severe at the moment (4)", "The pain is the worst imaginable (5)"] },
      { id: "q2", label: "2. Personal Care (Washing, Dressing etc.)", type: "radio", options: ["I can look after myself normally without causing extra pain (0)", "I can look after myself normally but it causes extra pain (1)", "It is painful to look after myself and I am slow and careful (2)", "I need some help but manage most of my personal care (3)", "I need help every day in most aspects of self care (4)", "I do not get dressed, wash with difficulty and stay in bed (5)"] },
      { id: "q3", label: "3. Lifting", type: "radio", options: ["I can lift heavy weights without extra pain (0)", "I can lift heavy weights but it causes extra pain (1)", "Pain prevents me from lifting heavy weights off the floor (2)", "Pain prevents me from lifting heavy weights but I can manage if conveniently positioned (3)", "I can lift only very light weights (4)", "I cannot lift or carry anything at all (5)"] },
      { id: "q4", label: "4. Reading", type: "radio", options: ["I can read as much as I want with no pain in my neck (0)", "I can read as much as I want with slight pain in my neck (1)", "I can read as much as I want with moderate pain in my neck (2)", "I cannot read as much as I want because of moderate pain (3)", "I can hardly read at all because of severe pain in my neck (4)", "I cannot read at all (5)"] },
      { id: "q5", label: "5. Headaches", type: "radio", options: ["I have no headaches at all (0)", "I have slight headaches which come infrequently (1)", "I have moderate headaches which come infrequently (2)", "I have moderate headaches which come frequently (3)", "I have severe headaches which come frequently (4)", "I have headaches almost all the time (5)"] },
      { id: "q6", label: "6. Concentration", type: "radio", options: ["I can concentrate fully when I want to without difficulty (0)", "I can concentrate fully when I want to with slight difficulty (1)", "I have a fair degree of difficulty concentrating (2)", "I have a lot of difficulty concentrating (3)", "I have a great deal of difficulty concentrating (4)", "I cannot concentrate at all (5)"] },
      { id: "q7", label: "7. Work", type: "radio", options: ["I can do as much work as I want to (0)", "I can only do my usual work but no more (1)", "I can do most of my usual work but no more (2)", "I cannot do my usual work (3)", "I can hardly do any work at all (4)", "I cannot do any work at all (5)"] },
      { id: "q8", label: "8. Driving", type: "radio", options: ["I can drive my car without any neck pain (0)", "I can drive my car as long as I want with slight pain (1)", "I can drive my car as long as I want with moderate pain (2)", "I cannot drive my car as long as I want because of moderate pain (3)", "I can hardly drive at all because of severe pain (4)", "I cannot drive my car at all (5)"] },
      { id: "q9", label: "9. Sleeping", type: "radio", options: ["I have no trouble sleeping (0)", "My sleep is slightly disturbed (less than 1 hour sleepless) (1)", "My sleep is mildly disturbed (1–2 hours sleepless) (2)", "My sleep is moderately disturbed (2–3 hours sleepless) (3)", "My sleep is greatly disturbed (3–5 hours sleepless) (4)", "My sleep is completely disturbed (5–7 hours sleepless) (5)"] },
      { id: "q10", label: "10. Recreation", type: "radio", options: ["I am able to engage in all my recreation activities (0)", "I can engage in all recreation with some pain in my neck (1)", "I can engage in most recreation, but not all (2)", "I can engage in a few of my recreational activities (3)", "I can hardly do any recreational activities (4)", "I cannot do any recreational activities at all (5)"] },
    ],
    calculate: (answers) => {
      const total = ["q1","q2","q3","q4","q5","q6","q7","q8","q9","q10"].reduce((sum, id) => sum + Number(answers[id] || 0), 0);
      return { score: Math.round((total / 50) * 100), max: 100 };
    },
    interpret: (score) => {
      if (score <= 8) return { label: "No Disability", color: "#34d399" };
      if (score <= 28) return { label: "Mild Disability", color: "#86efac" };
      if (score <= 48) return { label: "Moderate Disability", color: "#fbbf24" };
      if (score <= 64) return { label: "Severe Disability", color: "#fb923c" };
      return { label: "Complete Disability", color: "#f87171" };
    },
  },

  START_BACK: {
    name: "STarT Back Screening Tool",
    shortName: "STarT",
    category: "Spine",
    description: "9-item screening tool for low back pain. Stratifies patients into low, medium, or high risk for persistent disabling LBP. NICE recommended. Changes entire management pathway.",
    mcid: "N/A — risk stratification tool",
    mdc: "N/A",
    timeToComplete: "2 min",
    items: 9,
    population: "Acute & chronic low back pain",
    reference: "Hill JC et al. Lancet. 2011;378(9802):1560–1571. DOI: 10.1016/S0140-6736(11)60937-9",
    maxScore: 9,
    questions: [
      { id: "q1", label: "1. My back pain has spread down my leg(s) at some time in the last 2 weeks", type: "radio", options: ["Agree (1)", "Disagree (0)"] },
      { id: "q2", label: "2. I have had pain in the shoulder or neck at some time in the last 2 weeks", type: "radio", options: ["Agree (1)", "Disagree (0)"] },
      { id: "q3", label: "3. I have only walked short distances because of my back pain", type: "radio", options: ["Agree (1)", "Disagree (0)"] },
      { id: "q4", label: "4. In the last 2 weeks, I have dressed more slowly than usual because of back pain", type: "radio", options: ["Agree (1)", "Disagree (0)"] },
      { id: "q5", label: "5. It's not really safe for a person with a condition like mine to be physically active", type: "radio", options: ["Agree (1)", "Disagree (0)"] },
      { id: "q6", label: "6. Worrying thoughts have been going through my mind a lot of the time", type: "radio", options: ["Agree (1)", "Disagree (0)"] },
      { id: "q7", label: "7. I feel that my back pain is terrible and it's never going to get any better", type: "radio", options: ["Agree (1)", "Disagree (0)"] },
      { id: "q8", label: "8. In general I have not enjoyed all the things I used to enjoy", type: "radio", options: ["Agree (1)", "Disagree (0)"] },
      { id: "q9", label: "9. Overall, how bothersome has your back pain been in the last 2 weeks?", type: "radio", options: ["Not at all / Slightly / Moderately (0)", "Very much / Extremely (1)"] },
    ],
    calculate: (answers) => {
      const total = ["q1","q2","q3","q4","q5","q6","q7","q8","q9"].reduce((sum, id) => sum + Number(answers[id] || 0), 0);
      const psychosocial = ["q5","q6","q7","q8","q9"].reduce((sum, id) => sum + Number(answers[id] || 0), 0);
      return { score: total, max: 9, psychosocial };
    },
    interpret: (score) => {
      if (score <= 3) return { label: "Low Risk — Self-management advice", color: "#34d399" };
      if (score <= 8) return { label: "Medium Risk — Targeted physiotherapy", color: "#fbbf24" };
      return { label: "High Risk — Psychologically informed PT", color: "#f87171" };
    },
  },

  FABQ: {
    name: "FABQ — Fear Avoidance Beliefs Questionnaire",
    shortName: "FABQ",
    category: "Spine",
    description: "16-item questionnaire measuring fear-avoidance beliefs about physical activity and work. Predicts chronicity, work absence, and disability. Key yellow flag screening tool.",
    mcid: "11 points (physical subscale)",
    mdc: "N/A",
    timeToComplete: "5 min",
    items: 16,
    population: "Low back pain — psychosocial screening",
    reference: "Waddell G et al. Pain. 1993;52(2):157–168. DOI: 10.1016/0304-3959(93)90127-B",
    maxScore: 96,
    questions: [
      { id: "q1", label: "1. My pain was caused by physical activity", type: "radio", options: ["Completely disagree (0)","(1)","(2)","(3)","(4)","(5)","Completely agree (6)"] },
      { id: "q2", label: "2. Physical activity makes my pain worse", type: "radio", options: ["Completely disagree (0)","(1)","(2)","(3)","(4)","(5)","Completely agree (6)"] },
      { id: "q3", label: "3. Physical activity might harm my back", type: "radio", options: ["Completely disagree (0)","(1)","(2)","(3)","(4)","(5)","Completely agree (6)"] },
      { id: "q4", label: "4. I should not do physical activities which might make my pain worse", type: "radio", options: ["Completely disagree (0)","(1)","(2)","(3)","(4)","(5)","Completely agree (6)"] },
      { id: "q5", label: "5. I cannot do physical activities which might make my pain worse", type: "radio", options: ["Completely disagree (0)","(1)","(2)","(3)","(4)","(5)","Completely agree (6)"] },
      { id: "q6", label: "6. My pain was caused by my work or by an accident at work", type: "radio", options: ["Completely disagree (0)","(1)","(2)","(3)","(4)","(5)","Completely agree (6)"] },
      { id: "q7", label: "7. My work aggravated my pain", type: "radio", options: ["Completely disagree (0)","(1)","(2)","(3)","(4)","(5)","Completely agree (6)"] },
      { id: "q8", label: "8. I have a claim for compensation for my pain", type: "radio", options: ["Completely disagree (0)","(1)","(2)","(3)","(4)","(5)","Completely agree (6)"] },
      { id: "q9", label: "9. My work is too heavy for me", type: "radio", options: ["Completely disagree (0)","(1)","(2)","(3)","(4)","(5)","Completely agree (6)"] },
      { id: "q10", label: "10. My work makes or would make my pain worse", type: "radio", options: ["Completely disagree (0)","(1)","(2)","(3)","(4)","(5)","Completely agree (6)"] },
      { id: "q11", label: "11. My work might harm my back", type: "radio", options: ["Completely disagree (0)","(1)","(2)","(3)","(4)","(5)","Completely agree (6)"] },
      { id: "q12", label: "12. I should not do my normal work with my present pain", type: "radio", options: ["Completely disagree (0)","(1)","(2)","(3)","(4)","(5)","Completely agree (6)"] },
      { id: "q13", label: "13. I cannot do my normal work with my present pain", type: "radio", options: ["Completely disagree (0)","(1)","(2)","(3)","(4)","(5)","Completely agree (6)"] },
      { id: "q14", label: "14. I cannot do my normal work till my pain is treated", type: "radio", options: ["Completely disagree (0)","(1)","(2)","(3)","(4)","(5)","Completely agree (6)"] },
      { id: "q15", label: "15. I do not think that I will be back to my normal work within 3 months", type: "radio", options: ["Completely disagree (0)","(1)","(2)","(3)","(4)","(5)","Completely agree (6)"] },
      { id: "q16", label: "16. I do not think that I will ever be able to go back to that work", type: "radio", options: ["Completely disagree (0)","(1)","(2)","(3)","(4)","(5)","Completely agree (6)"] },
    ],
    calculate: (answers) => {
      const physical = ["q2","q3","q4","q5"].reduce((s, id) => s + Number(answers[id] || 0), 0);
      const work = ["q6","q7","q9","q10","q11","q12","q13","q14","q15"].reduce((s, id) => s + Number(answers[id] || 0), 0);
      return { score: physical + work, max: 96, physical, work };
    },
    interpret: (score) => {
      if (score <= 28) return { label: "Low Fear-Avoidance", color: "#34d399" };
      if (score <= 56) return { label: "Moderate Fear-Avoidance", color: "#fbbf24" };
      return { label: "High Fear-Avoidance — Psychosocial input needed", color: "#f87171" };
    },
  },

  // ══════════════════════════════════════════
  // UPPER LIMB
  // ══════════════════════════════════════════

  DASH: {
    name: "DASH — Disabilities of Arm, Shoulder and Hand",
    shortName: "DASH",
    category: "Upper Limb",
    description: "30-item scale measuring upper extremity disability. Each item scored 1–5. Score 0–100 (higher = more disability).",
    mcid: "10.2 points",
    mdc: "12.7 points",
    timeToComplete: "10 min",
    items: 30,
    population: "Any upper extremity condition",
    reference: "Beaton DE et al. J Hand Ther. 2001;14(2):84–94. DOI: 10.1016/S0894-1130(01)80052-8",
    maxScore: 100,
    questions: [
      "Open a tight or new jar",
      "Write",
      "Turn a key",
      "Prepare a meal",
      "Push open a heavy door",
      "Place an object on a shelf above your head",
      "Do heavy household chores (washing walls, floors)",
      "Garden or do yard work",
      "Make a bed",
      "Carry a shopping bag or briefcase",
      "Carry a heavy object (over 10 lbs)",
      "Change a lightbulb overhead",
      "Wash or blow dry your hair",
      "Wash your back",
      "Put on a pullover sweater",
      "Use a knife to cut food",
      "Recreational activities requiring little effort (cardplaying)",
      "Recreational activities with force/impact (golf, hammering, tennis)",
      "Recreational activities where you move arm freely (frisbee, badminton)",
      "Manage transportation needs",
      "Sexual activities",
      "How much has your arm/shoulder/hand problem interfered with social activities?",
      "Were you limited in work or daily activities due to arm/shoulder/hand problem?",
      "Arm, shoulder or hand pain",
      "Arm, shoulder or hand pain during specific activity",
      "Tingling (pins and needles) in arm, shoulder or hand",
      "Weakness in arm, shoulder or hand",
      "Stiffness in arm, shoulder or hand",
      "Difficulty sleeping due to arm/shoulder/hand pain",
      "I feel less capable/confident because of my arm/shoulder/hand problem",
    ].map((label, i) => ({
      id: `q${i + 1}`,
      label: `${i + 1}. ${label}`,
      type: "radio",
      options: i < 21
        ? ["No difficulty (1)", "Mild difficulty (2)", "Moderate difficulty (3)", "Severe difficulty (4)", "Unable (5)"]
        : i === 21
        ? ["Not at all (1)", "Slightly (2)", "Moderately (3)", "Quite a bit (4)", "Extremely (5)"]
        : i === 22
        ? ["Not limited at all (1)", "Slightly limited (2)", "Moderately limited (3)", "Very limited (4)", "Unable (5)"]
        : ["None (1)", "Mild (2)", "Moderate (3)", "Severe (4)", "Extreme (5)"],
    })),
    calculate: (answers) => {
      const vals = Array.from({ length: 30 }, (_, i) => Number(answers[`q${i + 1}`] || 0) + 1);
      const answered = vals.filter((v) => v > 1);
      if (answered.length < 27) return { score: null, max: 100 };
      const score = ((vals.reduce((a, b) => a + b, 0) / vals.length) - 1) / 4 * 100;
      return { score: Math.round(score), max: 100 };
    },
    interpret: (score) => {
      if (score <= 25) return { label: "Mild Disability", color: "#34d399" };
      if (score <= 50) return { label: "Moderate Disability", color: "#fbbf24" };
      if (score <= 75) return { label: "Severe Disability", color: "#fb923c" };
      return { label: "Very Severe Disability", color: "#f87171" };
    },
  },

  ASES: {
    name: "ASES — American Shoulder & Elbow Surgeons Score",
    shortName: "ASES",
    category: "Upper Limb",
    description: "Gold standard shoulder outcome measure. Pain VAS (50%) + 10 functional activities (50%) = 100 points. Higher = better. Used worldwide for rotator cuff, instability, arthroplasty.",
    mcid: "12 points",
    mdc: "17 points",
    timeToComplete: "5 min",
    items: 11,
    population: "Any shoulder condition",
    reference: "Michener LA et al. J Shoulder Elbow Surg. 2002;11(6):574–581. DOI: 10.1067/mse.2002.127096",
    maxScore: 100,
    questions: [
      { id: "pain", label: "Pain — How bad is your pain today? (0 = No pain, 10 = Pain as bad as it can be)", type: "slider", min: 0, max: 10 },
      { id: "f1", label: "Function 1 — Put on a coat", type: "radio", options: ["Unable (0)", "Very difficult (1)", "Somewhat difficult (2)", "Not difficult (3)"] },
      { id: "f2", label: "Function 2 — Sleep on your painful or affected side", type: "radio", options: ["Unable (0)", "Very difficult (1)", "Somewhat difficult (2)", "Not difficult (3)"] },
      { id: "f3", label: "Function 3 — Wash back / do up bra in back", type: "radio", options: ["Unable (0)", "Very difficult (1)", "Somewhat difficult (2)", "Not difficult (3)"] },
      { id: "f4", label: "Function 4 — Manage toileting", type: "radio", options: ["Unable (0)", "Very difficult (1)", "Somewhat difficult (2)", "Not difficult (3)"] },
      { id: "f5", label: "Function 5 — Comb hair", type: "radio", options: ["Unable (0)", "Very difficult (1)", "Somewhat difficult (2)", "Not difficult (3)"] },
      { id: "f6", label: "Function 6 — Reach a high shelf", type: "radio", options: ["Unable (0)", "Very difficult (1)", "Somewhat difficult (2)", "Not difficult (3)"] },
      { id: "f7", label: "Function 7 — Lift 10 lbs above shoulder", type: "radio", options: ["Unable (0)", "Very difficult (1)", "Somewhat difficult (2)", "Not difficult (3)"] },
      { id: "f8", label: "Function 8 — Throw a ball overhand", type: "radio", options: ["Unable (0)", "Very difficult (1)", "Somewhat difficult (2)", "Not difficult (3)"] },
      { id: "f9", label: "Function 9 — Do usual work", type: "radio", options: ["Unable (0)", "Very difficult (1)", "Somewhat difficult (2)", "Not difficult (3)"] },
      { id: "f10", label: "Function 10 — Do usual sport", type: "radio", options: ["Unable (0)", "Very difficult (1)", "Somewhat difficult (2)", "Not difficult (3)"] },
    ],
    calculate: (answers) => {
      const pain = Number(answers.pain ?? 5);
      const painScore = ((10 - pain) / 10) * 50;
      const funcTotal = ["f1","f2","f3","f4","f5","f6","f7","f8","f9","f10"].reduce((s, id) => s + Number(answers[id] || 0), 0);
      const funcScore = (funcTotal / 30) * 50;
      return { score: Math.round(painScore + funcScore), max: 100 };
    },
    interpret: (score) => {
      if (score >= 80) return { label: "Excellent", color: "#34d399" };
      if (score >= 60) return { label: "Good", color: "#86efac" };
      if (score >= 40) return { label: "Fair", color: "#fbbf24" };
      return { label: "Poor", color: "#f87171" };
    },
  },

  WOSI: {
    name: "WOSI — Western Ontario Shoulder Instability Index",
    shortName: "WOSI",
    category: "Upper Limb",
    description: "21-item VAS-based scale specific to shoulder instability. 4 domains: Physical symptoms, Sport/Recreation, Work, Lifestyle/Social. Score 0–2100 (lower = better), converted to % of normal.",
    mcid: "10.4% (220 points raw)",
    mdc: "N/A",
    timeToComplete: "8 min",
    items: 21,
    population: "Shoulder instability — Bankart, MDI, post-surgical",
    reference: "Kirkley A et al. Am J Sports Med. 1998;26(6):764–772. DOI: 10.1177/03635465980260060501",
    maxScore: 100,
    questions: Array.from({ length: 21 }, (_, i) => ({
      id: `q${i + 1}`,
      label: [
        "1. How much pain do you experience in your shoulder during activities above your head?",
        "2. How much does your shoulder click, pop, or grind?",
        "3. How much do you feel weakness in your shoulder?",
        "4. How much fatigue does your shoulder experience?",
        "5. How much does your shoulder affect your ability to do any heavy lifting?",
        "6. How much does your shoulder affect your ability to carry objects?",
        "7. How much does your shoulder affect your ability to do push/pull activities?",
        "8. How much difficulty do you have using your arm in an overhead position?",
        "9. How much does the instability of your shoulder affect your sport?",
        "10. How much does your shoulder affect your ability to throw an object?",
        "11. How much does your shoulder limit your ability to perform sport at your desired level?",
        "12. How much does your shoulder affect your ability to do your usual work?",
        "13. How much does your shoulder affect your career choices or specific work tasks?",
        "14. How much do you have to protect your shoulder in daily activities?",
        "15. How much difficulty do you have with overhead activities in daily life?",
        "16. How much does your shoulder affect your ability to dress yourself?",
        "17. How much does your shoulder affect your ability to sleep?",
        "18. How anxious or fearful are you about your shoulder?",
        "19. How self-conscious are you about your shoulder?",
        "20. How much do you feel that your shoulder affects your relationship with others?",
        "21. Overall, how much does your shoulder affect your quality of life?",
      ][i],
      type: "slider",
      min: 0,
      max: 100,
    })),
    calculate: (answers) => {
      const total = Array.from({ length: 21 }, (_, i) => Number(answers[`q${i + 1}`] || 0)).reduce((a, b) => a + b, 0);
      const percentage = Math.round(((2100 - total) / 2100) * 100);
      return { score: percentage, max: 100 };
    },
    interpret: (score) => {
      if (score >= 80) return { label: "Excellent Stability", color: "#34d399" };
      if (score >= 60) return { label: "Good", color: "#86efac" };
      if (score >= 40) return { label: "Moderate Instability", color: "#fbbf24" };
      return { label: "Severe Instability", color: "#f87171" };
    },
  },

  // ══════════════════════════════════════════
  // LOWER LIMB
  // ══════════════════════════════════════════

  LEFS: {
    name: "LEFS — Lower Extremity Functional Scale",
    shortName: "LEFS",
    category: "Lower Limb",
    description: "20-item scale for lower extremity function. Each item scored 0–4. Higher = better function.",
    mcid: "9 points",
    mdc: "9 points",
    timeToComplete: "5 min",
    items: 20,
    population: "Any lower extremity condition",
    reference: "Binkley JM et al. Phys Ther. 1999;79(4):371–383. DOI: 10.1093/ptj/79.4.371",
    maxScore: 80,
    questions: [
      "Any of your usual work, housework or school activities",
      "Your usual hobbies, recreational or sporting activities",
      "Getting into or out of the bath",
      "Walking between rooms",
      "Putting on your shoes or socks",
      "Squatting",
      "Lifting an object, like a bag of groceries from the floor",
      "Performing light activities around your home",
      "Performing heavy activities around your home",
      "Getting into or out of a car",
      "Walking 2 blocks",
      "Walking a mile",
      "Going up or down 10 stairs (about 1 flight of stairs)",
      "Standing for 1 hour",
      "Sitting for 1 hour",
      "Running on even ground",
      "Running on uneven ground",
      "Making sharp turns while running fast",
      "Hopping",
      "Rolling over in bed",
    ].map((label, i) => ({
      id: `q${i + 1}`,
      label: `${i + 1}. ${label}`,
      type: "radio",
      options: ["Extreme difficulty or unable (0)", "Quite a bit of difficulty (1)", "Moderate difficulty (2)", "A little bit of difficulty (3)", "No difficulty (4)"],
    })),
    calculate: (answers) => {
      const total = Array.from({ length: 20 }, (_, i) => Number(answers[`q${i + 1}`] || 0)).reduce((a, b) => a + b, 0);
      return { score: total, max: 80 };
    },
    interpret: (score) => {
      if (score >= 60) return { label: "Minimal Limitation", color: "#34d399" };
      if (score >= 40) return { label: "Moderate Limitation", color: "#fbbf24" };
      if (score >= 20) return { label: "Severe Limitation", color: "#fb923c" };
      return { label: "Very Severe Limitation", color: "#f87171" };
    },
  },

  KOOS: {
    name: "KOOS — Knee Injury & Osteoarthritis Outcome Score",
    shortName: "KOOS",
    category: "Lower Limb",
    description: "42-item gold standard for knee conditions. 5 subscales: Pain, Symptoms, ADL, Sport/Recreation, QoL. Score 0–100 per subscale (100 = no problems). Superior to WOMAC for active patients.",
    mcid: "8–10 points per subscale",
    mdc: "12 points",
    timeToComplete: "10 min",
    items: 42,
    population: "ACL, meniscus, knee OA, post-surgical knee",
    reference: "Roos EM et al. J Orthop Sports Phys Ther. 1998;28(2):88–96. DOI: 10.2519/jospt.1998.28.2.88",
    maxScore: 100,
    questions: [
      { id: "p1", label: "PAIN — How often do you experience knee pain?", type: "radio", options: ["Never (0)", "Monthly (1)", "Weekly (2)", "Daily (3)", "Always (4)"] },
      { id: "p2", label: "PAIN — Twisting/pivoting on your knee", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p3", label: "PAIN — Straightening knee fully", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p4", label: "PAIN — Bending knee fully", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p5", label: "PAIN — Walking on flat surface", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p6", label: "PAIN — Going up or down stairs", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p7", label: "PAIN — At night while in bed", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p8", label: "PAIN — Sitting or lying", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p9", label: "PAIN — Standing upright", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "s1", label: "SYMPTOMS — Morning stiffness severity", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "s2", label: "SYMPTOMS — Stiffness after sitting/lying/resting", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "s3", label: "SYMPTOMS — Swelling in knee", type: "radio", options: ["Never (0)", "Rarely (1)", "Sometimes (2)", "Often (3)", "Always (4)"] },
      { id: "s4", label: "SYMPTOMS — Grinding/clicking/any noise on knee movement", type: "radio", options: ["Never (0)", "Rarely (1)", "Sometimes (2)", "Often (3)", "Always (4)"] },
      { id: "s5", label: "SYMPTOMS — Knee catching or hanging up when moving", type: "radio", options: ["Never (0)", "Rarely (1)", "Sometimes (2)", "Often (3)", "Always (4)"] },
      { id: "s6", label: "SYMPTOMS — Straighten knee fully", type: "radio", options: ["Never (0)", "Rarely (1)", "Sometimes (2)", "Often (3)", "Always (4)"] },
      { id: "s7", label: "SYMPTOMS — Bend knee fully", type: "radio", options: ["Never (0)", "Rarely (1)", "Sometimes (2)", "Often (3)", "Always (4)"] },
      { id: "a1", label: "ADL — Descending stairs", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a2", label: "ADL — Ascending stairs", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a3", label: "ADL — Rising from sitting", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a4", label: "ADL — Standing", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a5", label: "ADL — Bending to floor/pick up object", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a6", label: "ADL — Walking on flat surface", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a7", label: "ADL — Getting in/out of car", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a8", label: "ADL — Going shopping", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a9", label: "ADL — Putting on socks/stockings", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a10", label: "ADL — Rising from bed", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a11", label: "ADL — Taking off socks/stockings", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a12", label: "ADL — Lying in bed (turning over, maintaining knee position)", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a13", label: "ADL — Getting in/out of bath", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a14", label: "ADL — Sitting", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a15", label: "ADL — Getting on/off toilet", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a16", label: "ADL — Heavy domestic duties", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a17", label: "ADL — Light domestic duties", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "sp1", label: "SPORT — Squatting", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "sp2", label: "SPORT — Running", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "sp3", label: "SPORT — Jumping", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "sp4", label: "SPORT — Twisting/pivoting on your injured knee", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "sp5", label: "SPORT — Kneeling", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "q1", label: "QoL — How often are you aware of your knee problem?", type: "radio", options: ["Never (0)", "Monthly (1)", "Weekly (2)", "Daily (3)", "Constantly (4)"] },
      { id: "q2", label: "QoL — Have you modified your lifestyle to avoid potentially damaging activities?", type: "radio", options: ["Not at all (0)", "Mildly (1)", "Moderately (2)", "Severely (3)", "Totally (4)"] },
      { id: "q3", label: "QoL — How much are you troubled with lack of confidence in your knee?", type: "radio", options: ["Not at all (0)", "Mildly (1)", "Moderately (2)", "Severely (3)", "Extremely (4)"] },
      { id: "q4", label: "QoL — In general, how much difficulty do you have with your knee?", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
    ],
    calculate: (answers) => {
      const pain = ["p1","p2","p3","p4","p5","p6","p7","p8","p9"].reduce((s, id) => s + Number(answers[id] || 0), 0);
      const symp = ["s1","s2","s3","s4","s5","s6","s7"].reduce((s, id) => s + Number(answers[id] || 0), 0);
      const adl = Array.from({ length: 17 }, (_, i) => Number(answers[`a${i + 1}`] || 0)).reduce((a, b) => a + b, 0);
      const sport = ["sp1","sp2","sp3","sp4","sp5"].reduce((s, id) => s + Number(answers[id] || 0), 0);
      const qol = ["q1","q2","q3","q4"].reduce((s, id) => s + Number(answers[id] || 0), 0);
      const painScore = Math.round(100 - (pain / 36) * 100);
      const sympScore = Math.round(100 - (symp / 28) * 100);
      const adlScore = Math.round(100 - (adl / 68) * 100);
      const sportScore = Math.round(100 - (sport / 20) * 100);
      const qolScore = Math.round(100 - (qol / 16) * 100);
      const avg = Math.round((painScore + sympScore + adlScore + sportScore + qolScore) / 5);
      return { score: avg, max: 100, painScore, sympScore, adlScore, sportScore, qolScore };
    },
    interpret: (score) => {
      if (score >= 75) return { label: "Good to Excellent", color: "#34d399" };
      if (score >= 50) return { label: "Moderate", color: "#fbbf24" };
      if (score >= 25) return { label: "Poor", color: "#fb923c" };
      return { label: "Very Poor", color: "#f87171" };
    },
  },

  WOMAC: {
    name: "WOMAC — Western Ontario and McMaster Universities",
    shortName: "WOMAC",
    category: "Lower Limb",
    description: "24-item scale for hip/knee osteoarthritis. Pain (5), Stiffness (2), Function (17). Higher score = worse symptoms.",
    mcid: "12 points",
    mdc: "15 points",
    timeToComplete: "8 min",
    items: 24,
    population: "Hip/Knee osteoarthritis — best for older/less active patients",
    reference: "Bellamy N et al. J Rheumatol. 1988;15(12):1833–1840. PMID: 3068365",
    maxScore: 96,
    questions: [
      { id: "p1", label: "PAIN — Walking on flat surface", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p2", label: "PAIN — Going up or down stairs", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p3", label: "PAIN — At night while in bed", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p4", label: "PAIN — Sitting or lying", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p5", label: "PAIN — Standing upright", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "s1", label: "STIFFNESS — Morning stiffness", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "s2", label: "STIFFNESS — Stiffness during the day", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f1", label: "FUNCTION — Descending stairs", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f2", label: "FUNCTION — Ascending stairs", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f3", label: "FUNCTION — Rising from sitting", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f4", label: "FUNCTION — Standing", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f5", label: "FUNCTION — Bending to floor", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f6", label: "FUNCTION — Walking on flat surface", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f7", label: "FUNCTION — Getting in/out of car", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f8", label: "FUNCTION — Going shopping", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f9", label: "FUNCTION — Putting on socks/stockings", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f10", label: "FUNCTION — Rising from bed", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f11", label: "FUNCTION — Taking off socks/stockings", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f12", label: "FUNCTION — Lying in bed", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f13", label: "FUNCTION — Getting in/out of bath", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f14", label: "FUNCTION — Sitting", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f15", label: "FUNCTION — Getting on/off toilet", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f16", label: "FUNCTION — Heavy domestic duties", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "f17", label: "FUNCTION — Light domestic duties", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
    ],
    calculate: (answers) => {
      const pain = ["p1","p2","p3","p4","p5"].reduce((s, id) => s + Number(answers[id] || 0), 0);
      const stiff = ["s1","s2"].reduce((s, id) => s + Number(answers[id] || 0), 0);
      const func = Array.from({ length: 17 }, (_, i) => Number(answers[`f${i + 1}`] || 0)).reduce((a, b) => a + b, 0);
      return { score: pain + stiff + func, max: 96 };
    },
    interpret: (score) => {
      if (score <= 24) return { label: "Minimal Symptoms", color: "#34d399" };
      if (score <= 48) return { label: "Moderate Symptoms", color: "#fbbf24" };
      if (score <= 72) return { label: "Severe Symptoms", color: "#fb923c" };
      return { label: "Extreme Symptoms", color: "#f87171" };
    },
  },

  IKDC: {
    name: "IKDC — International Knee Documentation Committee",
    shortName: "IKDC",
    category: "Lower Limb",
    description: "18-item subjective knee evaluation. Gold standard for ACL rehabilitation and return-to-sport decisions. Score 0–100 (higher = better function). Widely used in sports knee RCTs.",
    mcid: "11.5 points",
    mdc: "16.7 points",
    timeToComplete: "7 min",
    items: 18,
    population: "ACL, meniscus, ligament injuries, sports knee",
    reference: "Irrgang JJ et al. Am J Sports Med. 2001;29(5):600–613. DOI: 10.1177/03635465010290051301",
    maxScore: 100,
    questions: [
      { id: "q1", label: "1. What is the highest level of activity you can perform without significant knee pain?", type: "radio", options: ["Very strenuous — jumping, pivoting (4)", "Strenuous — heavy physical labour, skiing, tennis (3)", "Moderate — moderate physical labour, running (2)", "Light — walking, housework, yard work (1)", "Unable to perform any of the above (0)"] },
      { id: "q2", label: "2. During the past 4 weeks, how often have you had pain?", type: "radio", options: ["Never (10)", "Monthly (8)", "Weekly (6)", "Daily (4)", "Constant (2)"] },
      { id: "q3", label: "3. If you have pain, how severe is it?", type: "radio", options: ["No pain (10)", "Mild (8)", "Moderate (6)", "Severe (4)", "Extreme (2)"] },
      { id: "q4", label: "4. How stiff or swollen was your knee during the past 4 weeks?", type: "radio", options: ["Not at all (4)", "Mildly (3)", "Moderately (2)", "Very (1)", "Extremely (0)"] },
      { id: "q5", label: "5. What is the highest level of activity you can perform without significant knee swelling?", type: "radio", options: ["Very strenuous activities (4)", "Strenuous activities (3)", "Moderate activities (2)", "Light activities (1)", "Unable to perform any activity (0)"] },
      { id: "q6", label: "6. During the past 4 weeks, did your knee lock or catch?", type: "radio", options: ["No (10)", "Yes (0)"] },
      { id: "q7", label: "7. What is the highest level of activity you can perform without significant knee giving way?", type: "radio", options: ["Very strenuous activities (4)", "Strenuous activities (3)", "Moderate activities (2)", "Light activities (1)", "Unable to perform any activity (0)"] },
      { id: "q8", label: "8. Stair climbing", type: "radio", options: ["No difficulty (4)", "Minimal difficulty (3)", "Moderate difficulty (2)", "Extreme difficulty (1)", "Unable (0)"] },
      { id: "q9", label: "9. Going down stairs", type: "radio", options: ["No difficulty (4)", "Minimal difficulty (3)", "Moderate difficulty (2)", "Extreme difficulty (1)", "Unable (0)"] },
      { id: "q10", label: "10. Kneeling on the front of knee", type: "radio", options: ["No difficulty (4)", "Minimal difficulty (3)", "Moderate difficulty (2)", "Extreme difficulty (1)", "Unable (0)"] },
      { id: "q11", label: "11. Squatting", type: "radio", options: ["No difficulty (4)", "Minimal difficulty (3)", "Moderate difficulty (2)", "Extreme difficulty (1)", "Unable (0)"] },
      { id: "q12", label: "12. Sitting with knee bent", type: "radio", options: ["No difficulty (4)", "Minimal difficulty (3)", "Moderate difficulty (2)", "Extreme difficulty (1)", "Unable (0)"] },
      { id: "q13", label: "13. Rising from a chair", type: "radio", options: ["No difficulty (4)", "Minimal difficulty (3)", "Moderate difficulty (2)", "Extreme difficulty (1)", "Unable (0)"] },
      { id: "q14", label: "14. Running straight ahead", type: "radio", options: ["No difficulty (4)", "Minimal difficulty (3)", "Moderate difficulty (2)", "Extreme difficulty (1)", "Unable (0)"] },
      { id: "q15", label: "15. Jumping and landing on involved leg", type: "radio", options: ["No difficulty (4)", "Minimal difficulty (3)", "Moderate difficulty (2)", "Extreme difficulty (1)", "Unable (0)"] },
      { id: "q16", label: "16. Stopping and starting quickly", type: "radio", options: ["No difficulty (4)", "Minimal difficulty (3)", "Moderate difficulty (2)", "Extreme difficulty (1)", "Unable (0)"] },
      { id: "q17", label: "17. How would you rate your knee function on a scale of 0–10? (10 = normal)", type: "slider", min: 0, max: 10 },
      { id: "q18", label: "18. What level of activity can you participate in?", type: "radio", options: ["Very strenuous (4)", "Strenuous (3)", "Moderate (2)", "Light (1)", "Unable (0)"] },
    ],
    calculate: (answers) => {
      const scores = [
        Number(answers.q1 || 0),
        Number(answers.q2 || 0),
        Number(answers.q3 || 0),
        Number(answers.q4 || 0),
        Number(answers.q5 || 0),
        Number(answers.q6 || 0),
        Number(answers.q7 || 0),
        Number(answers.q8 || 0),
        Number(answers.q9 || 0),
        Number(answers.q10 || 0),
        Number(answers.q11 || 0),
        Number(answers.q12 || 0),
        Number(answers.q13 || 0),
        Number(answers.q14 || 0),
        Number(answers.q15 || 0),
        Number(answers.q16 || 0),
        Number(answers.q17 || 0),
        Number(answers.q18 || 0),
      ];
      const total = scores.reduce((a, b) => a + b, 0);
      const score = Math.round((total / 87) * 100);
      return { score, max: 100 };
    },
    interpret: (score) => {
      if (score >= 75) return { label: "Good to Excellent", color: "#34d399" };
      if (score >= 50) return { label: "Fair", color: "#fbbf24" };
      return { label: "Poor", color: "#f87171" };
    },
  },

  LYSHOLM: {
    name: "Lysholm Knee Score",
    shortName: "Lysholm",
    category: "Lower Limb",
    description: "8-item scale specifically designed for knee ligament injuries. Covers limp, support, locking, instability, pain, swelling, stair climbing, squatting. Score 0–100 (higher = better).",
    mcid: "10 points",
    mdc: "N/A",
    timeToComplete: "4 min",
    items: 8,
    population: "ACL, PCL, ligament injuries, meniscal pathology",
    reference: "Lysholm J, Gillquist J. Am J Sports Med. 1982;10(3):150–154. DOI: 10.1177/036354658201000306",
    maxScore: 100,
    questions: [
      { id: "q1", label: "1. Limp", type: "radio", options: ["None (5)", "Slight or periodical (3)", "Severe and constant (0)"] },
      { id: "q2", label: "2. Support", type: "radio", options: ["Full support (5)", "Stick or crutch (2)", "Weight-bearing impossible (0)"] },
      { id: "q3", label: "3. Locking", type: "radio", options: ["No locking and no catching sensation (15)", "Catching sensation but no locking (10)", "Locking occasionally (6)", "Locking frequently (2)", "Locked joint on examination (0)"] },
      { id: "q4", label: "4. Instability", type: "radio", options: ["Never giving way (25)", "Rarely during athletics or vigorous activities (20)", "Frequently during athletics or vigorous activities (15)", "Occasionally in daily activities (10)", "Often in daily activities (5)", "Every step (0)"] },
      { id: "q5", label: "5. Pain", type: "radio", options: ["None (25)", "Inconstant and slight during vigorous activities (20)", "Marked during vigorous activities (15)", "Marked on or after walking >2 km (10)", "Marked on or after walking <2 km (5)", "Constant (0)"] },
      { id: "q6", label: "6. Swelling", type: "radio", options: ["None (10)", "With vigorous activities (6)", "With ordinary activities (2)", "Constant (0)"] },
      { id: "q7", label: "7. Stair Climbing", type: "radio", options: ["No problems (10)", "Slightly impaired (6)", "One step at a time (2)", "Impossible (0)"] },
      { id: "q8", label: "8. Squatting", type: "radio", options: ["No problems (5)", "Slightly impaired (4)", "Not past 90° (3)", "Impossible (0)"] },
    ],
    calculate: (answers) => {
      const total = ["q1","q2","q3","q4","q5","q6","q7","q8"].reduce((s, id) => s + Number(answers[id] || 0), 0);
      return { score: total, max: 100 };
    },
    interpret: (score) => {
      if (score >= 95) return { label: "Excellent", color: "#34d399" };
      if (score >= 84) return { label: "Good", color: "#86efac" };
      if (score >= 65) return { label: "Fair", color: "#fbbf24" };
      return { label: "Poor", color: "#f87171" };
    },
  },

  // ══════════════════════════════════════════
  // HIP
  // ══════════════════════════════════════════

  HOOS: {
    name: "HOOS — Hip disability & Osteoarthritis Outcome Score",
    shortName: "HOOS",
    category: "Hip",
    description: "40-item hip-specific outcome measure. 5 subscales: Pain, Symptoms, ADL, Sport/Recreation, QoL. Score 0–100 per subscale (100 = no problems). Gold standard for hip OA and post-THR.",
    mcid: "10 points per subscale",
    mdc: "12 points",
    timeToComplete: "10 min",
    items: 40,
    population: "Hip OA, THR, FAI, labral tears, hip dysplasia",
    reference: "Nilsdotter AK et al. BMC Musculoskelet Disord. 2003;4:10. DOI: 10.1186/1471-2474-4-10",
    maxScore: 100,
    questions: [
      { id: "p1", label: "PAIN — How often do you experience hip pain?", type: "radio", options: ["Never (0)", "Monthly (1)", "Weekly (2)", "Daily (3)", "Always (4)"] },
      { id: "p2", label: "PAIN — Straightening hip fully", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p3", label: "PAIN — Bending hip fully", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p4", label: "PAIN — Walking on flat surface", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p5", label: "PAIN — Going up or down stairs", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p6", label: "PAIN — At night while in bed", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p7", label: "PAIN — Sitting or lying", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p8", label: "PAIN — Standing upright", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p9", label: "PAIN — Walking on hard surface", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "p10", label: "PAIN — Walking on uneven surface", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "s1", label: "SYMPTOMS — Morning stiffness severity", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "s2", label: "SYMPTOMS — Stiffness after sitting/lying", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "s3", label: "SYMPTOMS — Grinding/clicking/any noise on hip movement", type: "radio", options: ["Never (0)", "Rarely (1)", "Sometimes (2)", "Often (3)", "Always (4)"] },
      { id: "s4", label: "SYMPTOMS — Hip catching or hanging up when moving", type: "radio", options: ["Never (0)", "Rarely (1)", "Sometimes (2)", "Often (3)", "Always (4)"] },
      { id: "s5", label: "SYMPTOMS — Straighten hip fully", type: "radio", options: ["Never (0)", "Rarely (1)", "Sometimes (2)", "Often (3)", "Always (4)"] },
      { id: "a1", label: "ADL — Descending stairs", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a2", label: "ADL — Ascending stairs", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a3", label: "ADL — Rising from sitting", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a4", label: "ADL — Standing", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a5", label: "ADL — Bending to floor", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a6", label: "ADL — Walking on flat surface", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a7", label: "ADL — Getting in/out of car", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a8", label: "ADL — Going shopping", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a9", label: "ADL — Putting on socks/stockings", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "a10", label: "ADL — Rising from bed", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "sp1", label: "SPORT — Squatting", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "sp2", label: "SPORT — Running", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "sp3", label: "SPORT — Jumping", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "sp4", label: "SPORT — Twisting/pivoting", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "sp5", label: "SPORT — Kneeling", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "q1", label: "QoL — How often are you aware of your hip problem?", type: "radio", options: ["Never (0)", "Monthly (1)", "Weekly (2)", "Daily (3)", "Constantly (4)"] },
      { id: "q2", label: "QoL — Have you modified lifestyle to avoid damaging activities?", type: "radio", options: ["Not at all (0)", "Mildly (1)", "Moderately (2)", "Severely (3)", "Totally (4)"] },
      { id: "q3", label: "QoL — How troubled are you by lack of confidence in hip?", type: "radio", options: ["Not at all (0)", "Mildly (1)", "Moderately (2)", "Severely (3)", "Extremely (4)"] },
      { id: "q4", label: "QoL — In general, how much difficulty do you have with your hip?", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
      { id: "q5", label: "QoL — Impact on daily life overall", type: "radio", options: ["None (0)", "Mild (1)", "Moderate (2)", "Severe (3)", "Extreme (4)"] },
    ],
    calculate: (answers) => {
      const pain = Array.from({ length: 10 }, (_, i) => Number(answers[`p${i + 1}`] || 0)).reduce((a, b) => a + b, 0);
      const symp = Array.from({ length: 5 }, (_, i) => Number(answers[`s${i + 1}`] || 0)).reduce((a, b) => a + b, 0);
      const adl = Array.from({ length: 10 }, (_, i) => Number(answers[`a${i + 1}`] || 0)).reduce((a, b) => a + b, 0);
      const sport = Array.from({ length: 5 }, (_, i) => Number(answers[`sp${i + 1}`] || 0)).reduce((a, b) => a + b, 0);
      const qol = Array.from({ length: 5 }, (_, i) => Number(answers[`q${i + 1}`] || 0)).reduce((a, b) => a + b, 0);
      const avg = Math.round((
        (100 - (pain / 40) * 100) +
        (100 - (symp / 20) * 100) +
        (100 - (adl / 40) * 100) +
        (100 - (sport / 20) * 100) +
        (100 - (qol / 20) * 100)
      ) / 5);
      return { score: avg, max: 100 };
    },
    interpret: (score) => {
      if (score >= 75) return { label: "Good to Excellent", color: "#34d399" };
      if (score >= 50) return { label: "Moderate", color: "#fbbf24" };
      if (score >= 25) return { label: "Poor", color: "#fb923c" };
      return { label: "Very Poor", color: "#f87171" };
    },
  },

  // ══════════════════════════════════════════
  // TENDINOPATHY
  // ══════════════════════════════════════════

  VISA_A: {
    name: "VISA-A — Victorian Institute of Sport Assessment (Achilles)",
    shortName: "VISA-A",
    category: "Tendinopathy",
    description: "8-item gold standard outcome measure for Achilles tendinopathy. Combines pain, function, and activity. Score 0–100 (100 = asymptomatic, fully active). Used in all major Achilles tendinopathy RCTs.",
    mcid: "12 points",
    mdc: "N/A",
    timeToComplete: "4 min",
    items: 8,
    population: "Achilles tendinopathy — mid-portion and insertional",
    reference: "Robinson JM et al. Br J Sports Med. 2001;35(5):335–341. DOI: 10.1136/bjsm.35.5.335",
    maxScore: 100,
    questions: [
      { id: "q1", label: "1. For how many minutes do you have stiffness in the Achilles region on rising in the morning?", type: "radio", options: ["0 min (10)", "1–10 min (7)", "11–30 min (5)", "31–60 min (3)", ">60 min (0)"] },
      { id: "q2", label: "2. Once warmed up for the day, do you have pain walking on flat ground?", type: "radio", options: ["No pain (10)", "Mild (7)", "Moderate (5)", "Severe (3)", "Very severe (0)"] },
      { id: "q3", label: "3. Do you have pain going down stairs with a normal walking pace?", type: "radio", options: ["No pain (10)", "Mild (7)", "Moderate (5)", "Severe (3)", "Very severe (0)"] },
      { id: "q4", label: "4. Do you have pain during or immediately after doing 10 single-leg heel raises from flat ground?", type: "radio", options: ["No pain (10)", "Mild (7)", "Moderate (5)", "Severe (3)", "Very severe (0)"] },
      { id: "q5", label: "5. After being seated for 30 minutes, do you have pain when you first stand up?", type: "radio", options: ["No pain (10)", "Mild (7)", "Moderate (5)", "Severe (3)", "Very severe (0)"] },
      { id: "q6", label: "6. Do you have pain during or after running?", type: "radio", options: ["No pain (10)", "Mild pain (7)", "Moderate pain (5)", "Severe pain (3)", "Very severe or unable to run (0)"] },
      { id: "q7", label: "7. Are you able to do single-leg heel raises?", type: "radio", options: ["≥20 reps without pain (20)", "10–19 reps (15)", "5–9 reps (10)", "1–4 reps (5)", "Unable (0)"] },
      { id: "q8", label: "8. What is your current level of sporting activity?", type: "radio", options: ["Full competition training — no limitations (20)", "Full training modified (15)", "Running ≥4x/week, flat only (10)", "Walking or reduced training (5)", "Not training or competing (0)"] },
    ],
    calculate: (answers) => {
      const total = ["q1","q2","q3","q4","q5","q6","q7","q8"].reduce((s, id) => s + Number(answers[id] || 0), 0);
      return { score: total, max: 100 };
    },
    interpret: (score) => {
      if (score >= 90) return { label: "Asymptomatic / Full Activity", color: "#34d399" };
      if (score >= 70) return { label: "Mild Tendinopathy", color: "#86efac" };
      if (score >= 50) return { label: "Moderate Tendinopathy", color: "#fbbf24" };
      return { label: "Severe Tendinopathy", color: "#f87171" };
    },
  },

  VISA_P: {
    name: "VISA-P — Victorian Institute of Sport Assessment (Patellar)",
    shortName: "VISA-P",
    category: "Tendinopathy",
    description: "8-item gold standard for patellar tendinopathy. Score 0–100 (100 = asymptomatic). Used in all major patellar tendinopathy RCTs including Nordic studies.",
    mcid: "13 points",
    mdc: "N/A",
    timeToComplete: "4 min",
    items: 8,
    population: "Patellar tendinopathy — jumper's knee",
    reference: "Visentini PJ et al. J Sci Med Sport. 1998;1(3):169–177. DOI: 10.1016/S1440-2440(98)80076-4",
    maxScore: 100,
    questions: [
      { id: "q1", label: "1. For how many minutes do you have stiffness in the knee on rising in the morning?", type: "radio", options: ["0 min (10)", "1–10 min (7)", "11–30 min (5)", "31–60 min (3)", ">60 min (0)"] },
      { id: "q2", label: "2. Do you have pain during or after doing 10 squats?", type: "radio", options: ["No pain (10)", "Mild (7)", "Moderate (5)", "Severe (3)", "Very severe (0)"] },
      { id: "q3", label: "3. Do you have pain going down stairs with a normal walking pace?", type: "radio", options: ["No pain (10)", "Mild (7)", "Moderate (5)", "Severe (3)", "Very severe (0)"] },
      { id: "q4", label: "4. Do you have knee pain after sitting for 30 minutes?", type: "radio", options: ["No pain (10)", "Mild (7)", "Moderate (5)", "Severe (3)", "Very severe (0)"] },
      { id: "q5", label: "5. Do you have pain during or after kneeling on the front of the kneecap?", type: "radio", options: ["No pain (10)", "Mild (7)", "Moderate (5)", "Severe (3)", "Very severe (0)"] },
      { id: "q6", label: "6. Do you have pain during or after running?", type: "radio", options: ["No pain (10)", "Mild (7)", "Moderate (5)", "Severe (3)", "Very severe (0)"] },
      { id: "q7", label: "7. Are you able to do single-leg decline squats?", type: "radio", options: ["≥20 reps without pain (20)", "10–19 reps (15)", "5–9 reps (10)", "1–4 reps (5)", "Unable (0)"] },
      { id: "q8", label: "8. What is your current level of sporting activity?", type: "radio", options: ["Full training and competition (20)", "Full training modified (15)", "Training modified — not jumping (10)", "Walking or ADL only (5)", "Not able to train (0)"] },
    ],
    calculate: (answers) => {
      const total = ["q1","q2","q3","q4","q5","q6","q7","q8"].reduce((s, id) => s + Number(answers[id] || 0), 0);
      return { score: total, max: 100 };
    },
    interpret: (score) => {
      if (score >= 90) return { label: "Asymptomatic / Full Activity", color: "#34d399" };
      if (score >= 70) return { label: "Mild Tendinopathy", color: "#86efac" };
      if (score >= 50) return { label: "Moderate Tendinopathy", color: "#fbbf24" };
      return { label: "Severe Tendinopathy", color: "#f87171" };
    },
  },

  // ══════════════════════════════════════════
  // BALANCE & NEURO
  // ══════════════════════════════════════════

  BERG: {
    name: "Berg Balance Scale",
    shortName: "BBS",
    category: "Balance & Neuro",
    description: "14-item scale assessing static and dynamic balance. Each item scored 0–4. Gold standard balance assessment for older adults and neurological conditions.",
    mcid: "4–6 points",
    mdc: "4.6 points",
    timeToComplete: "15 min",
    items: 14,
    population: "Elderly, stroke, Parkinson's, post-surgical, fall risk",
    reference: "Berg KO et al. Can J Public Health. 1992;83(Suppl 2):S7–11. PMID: 1468055",
    maxScore: 56,
    questions: [
      { id: "q1", label: "1. Sitting to standing", type: "radio", options: ["Unable without assist (0)", "Needs assist to stand or stabilize (1)", "Needs min aid to stand (2)", "Stands independently using hands (3)", "Stands without using hands and stabilizes independently (4)"] },
      { id: "q2", label: "2. Standing unsupported", type: "radio", options: ["Unable to stand 30 seconds unsupported (0)", "Needs several tries to stand 30 seconds (1)", "Able to stand 30 seconds unsupported (2)", "Able to stand 2 minutes with supervision (3)", "Able to stand safely 2 minutes (4)"] },
      { id: "q3", label: "3. Sitting unsupported", type: "radio", options: ["Unable to sit without support 10 seconds (0)", "Able to sit 10 seconds (1)", "Able to sit 30 seconds (2)", "Able to sit 2 minutes under supervision (3)", "Able to sit safely and securely 2 minutes (4)"] },
      { id: "q4", label: "4. Standing to sitting", type: "radio", options: ["Needs assist to sit (0)", "Sits independently but has uncontrolled descent (1)", "Uses back of legs against chair to control descent (2)", "Controls descent by using hands (3)", "Sits safely with minimal use of hands (4)"] },
      { id: "q5", label: "5. Transfers", type: "radio", options: ["Needs two people to assist or supervise (0)", "Needs one person to assist (1)", "Able to transfer with verbal cueing/supervision (2)", "Able to transfer safely with definite use of hands (3)", "Able to transfer safely with minor use of hands (4)"] },
      { id: "q6", label: "6. Standing with eyes closed", type: "radio", options: ["Needs help to keep from falling (0)", "Unable to keep eyes closed 3 seconds but stands safely (1)", "Able to stand 3 seconds (2)", "Able to stand 10 seconds with supervision (3)", "Able to stand 10 seconds safely (4)"] },
      { id: "q7", label: "7. Standing with feet together", type: "radio", options: ["Needs help to attain position and unable to hold (0)", "Needs help to attain and hold 15 seconds (1)", "Able to place feet together but unable to hold (2)", "Able to place feet together and stand 1 minute with supervision (3)", "Able to place feet together and stand 1 minute safely (4)"] },
      { id: "q8", label: "8. Reaching forward with outstretched arm", type: "radio", options: ["Loses balance while trying / needs external support (0)", "Reaches forward but needs supervision (1)", "Can reach forward more than 2 inches safely (2)", "Can reach forward more than 5 inches safely (3)", "Can reach forward confidently more than 10 inches (4)"] },
      { id: "q9", label: "9. Pick up object from floor", type: "radio", options: ["Unable to try / needs assist (0)", "Unable to pick up and needs supervision (1)", "Unable to pick up but reaches 1–2 inches and keeps balance (2)", "Able to pick up object but needs supervision (3)", "Able to pick up object safely (4)"] },
      { id: "q10", label: "10. Turning to look behind", type: "radio", options: ["Needs assist to keep from losing balance (0)", "Needs supervision when turning (1)", "Turns sideways only but maintains balance (2)", "Looks behind one side only (3)", "Looks behind from both sides (4)"] },
      { id: "q11", label: "11. Turn 360 degrees", type: "radio", options: ["Needs assistance (0)", "Needs close supervision (1)", "Able to turn 360 degrees safely but slowly (2)", "Able to turn 360 safely one side only in ≤4 seconds (3)", "Able to turn 360 degrees safely in ≤4 seconds each side (4)"] },
      { id: "q12", label: "12. Placing alternate foot on stool", type: "radio", options: ["Needs assistance to keep from falling (0)", "Able to complete less than 2 steps (1)", "Able to complete 4 steps without aid (2)", "Able to complete 8 steps with supervision (3)", "Able to stand independently and safely complete 8 steps (4)"] },
      { id: "q13", label: "13. Standing with one foot in front", type: "radio", options: ["Loses balance while stepping or standing (0)", "Needs help to step but can hold 15 seconds (1)", "Able to take small step independently and hold 30 seconds (2)", "Able to place foot ahead independently and hold 30 seconds (3)", "Able to place foot tandem independently and hold 30 seconds (4)"] },
      { id: "q14", label: "14. Standing on one leg", type: "radio", options: ["Unable to try or needs assistance (0)", "Tries to lift leg, unable to hold 3 seconds (1)", "Able to lift leg independently and hold ≥3 seconds (2)", "Able to lift leg independently and hold 5–10 seconds (3)", "Able to lift leg independently and hold more than 10 seconds (4)"] },
    ],
    calculate: (answers) => {
      const total = Array.from({ length: 14 }, (_, i) => Number(answers[`q${i + 1}`] || 0)).reduce((a, b) => a + b, 0);
      return { score: total, max: 56 };
    },
    interpret: (score) => {
      if (score >= 45) return { label: "Low Fall Risk", color: "#34d399" };
      if (score >= 35) return { label: "Medium Fall Risk — Supervision needed", color: "#fbbf24" };
      return { label: "High Fall Risk — Assisted ambulation", color: "#f87171" };
    },
  },

  TUG: {
    name: "TUG — Timed Up and Go Test",
    shortName: "TUG",
    category: "Balance & Neuro",
    description: "Functional mobility and fall risk test. Time taken to rise from chair, walk 3 metres, turn, walk back, and sit. Simple, reliable, widely used globally.",
    mcid: "2.9 seconds",
    mdc: "3.4 seconds",
    timeToComplete: "5 min",
    items: 1,
    population: "Elderly, neurological, post-surgical, fall risk assessment",
    reference: "Podsiadlo D, Richardson S. J Am Geriatr Soc. 1991;39(2):142–148. DOI: 10.1111/j.1532-5415.1991.tb01616.x",
    maxScore: 60,
    questions: [
      { id: "time", label: "Time taken to complete TUG test (seconds) — Rise from chair, walk 3m, turn, walk back, sit", type: "slider", min: 1, max: 60 },
    ],
    calculate: (answers) => ({ score: Number(answers.time ?? 12), max: 60 }),
    interpret: (score) => {
      if (score <= 10) return { label: "Normal — Low Fall Risk (<10 sec)", color: "#34d399" };
      if (score <= 13.5) return { label: "Mild Limitation — Monitor (10–13.5 sec)", color: "#86efac" };
      if (score <= 20) return { label: "Moderate Risk — Supervision needed (>13.5 sec)", color: "#fbbf24" };
      return { label: "High Fall Risk — Intervention required (>20 sec)", color: "#f87171" };
    },
  },

  MAS: {
    name: "Modified Ashworth Scale — Spasticity",
    shortName: "MAS",
    category: "Balance & Neuro",
    description: "6-point ordinal scale measuring muscle spasticity. Essential for neurological physiotherapy — stroke, SCI, TBI, CP, MS. Grade 0–4 (with 1+ between 1 and 2).",
    mcid: "1 grade",
    mdc: "N/A",
    timeToComplete: "5 min",
    items: 6,
    population: "Stroke, SCI, TBI, cerebral palsy, MS — spasticity assessment",
    reference: "Bohannon RW, Smith MB. Phys Ther. 1987;67(2):206–207. DOI: 10.1093/ptj/67.2.206",
    maxScore: 24,
    questions: [
      { id: "elbow_flex", label: "Elbow Flexors", type: "radio", options: ["0 — No increase in tone (0)", "1 — Slight increase, catch and release (1)", "1+ — Catch + minimal resistance through <50% ROM (2)", "2 — Marked increase through 50%+ ROM, easy to move (3)", "3 — Considerable increase, passive movement difficult (4)", "4 — Affected part rigid in flexion or extension (5)"] },
      { id: "elbow_ext", label: "Elbow Extensors", type: "radio", options: ["0 — No increase in tone (0)", "1 — Slight increase, catch and release (1)", "1+ — Catch + minimal resistance through <50% ROM (2)", "2 — Marked increase through 50%+ ROM (3)", "3 — Considerable increase, passive movement difficult (4)", "4 — Rigid (5)"] },
      { id: "wrist_flex", label: "Wrist Flexors", type: "radio", options: ["0 — No increase in tone (0)", "1 — Slight increase, catch and release (1)", "1+ — Catch + minimal resistance through <50% ROM (2)", "2 — Marked increase through 50%+ ROM (3)", "3 — Considerable increase (4)", "4 — Rigid (5)"] },
      { id: "knee_ext", label: "Knee Extensors", type: "radio", options: ["0 — No increase in tone (0)", "1 — Slight increase, catch and release (1)", "1+ — Catch + minimal resistance through <50% ROM (2)", "2 — Marked increase through 50%+ ROM (3)", "3 — Considerable increase, passive movement difficult (4)", "4 — Rigid (5)"] },
      { id: "ankle_pf", label: "Ankle Plantar Flexors", type: "radio", options: ["0 — No increase in tone (0)", "1 — Slight increase, catch and release (1)", "1+ — Catch + minimal resistance through <50% ROM (2)", "2 — Marked increase through 50%+ ROM (3)", "3 — Considerable increase (4)", "4 — Rigid (5)"] },
      { id: "hip_add", label: "Hip Adductors", type: "radio", options: ["0 — No increase in tone (0)", "1 — Slight increase, catch and release (1)", "1+ — Catch + minimal resistance through <50% ROM (2)", "2 — Marked increase through 50%+ ROM (3)", "3 — Considerable increase (4)", "4 — Rigid (5)"] },
    ],
    calculate: (answers) => {
      const total = ["elbow_flex","elbow_ext","wrist_flex","knee_ext","ankle_pf","hip_add"].reduce((s, id) => s + Number(answers[id] || 0), 0);
      return { score: total, max: 24 };
    },
    interpret: (score) => {
      if (score === 0) return { label: "No Spasticity", color: "#34d399" };
      if (score <= 6) return { label: "Mild Spasticity", color: "#86efac" };
      if (score <= 12) return { label: "Moderate Spasticity", color: "#fbbf24" };
      return { label: "Severe Spasticity", color: "#f87171" };
    },
  },

  // ══════════════════════════════════════════
  // PSYCHOLOGICAL
  // ══════════════════════════════════════════

  ACL_RSI: {
    name: "ACL-RSI — ACL Return to Sport after Injury Scale",
    shortName: "ACL-RSI",
    category: "Psychological",
    description: "12-item psychological readiness scale for return to sport after ACL injury. Score 0–100 (higher = more psychologically ready). Score <56 = not psychologically ready to return.",
    mcid: "N/A",
    mdc: "N/A",
    timeToComplete: "5 min",
    items: 12,
    population: "ACL reconstruction — return to sport decision making",
    reference: "Webster KE et al. Am J Sports Med. 2008;36(8):1496–1502. DOI: 10.1177/0363546508314613",
    maxScore: 100,
    questions: Array.from({ length: 12 }, (_, i) => ({
      id: `q${i + 1}`,
      label: [
        "1. I am afraid of re-injuring my knee by returning to sport",
        "2. I am afraid of experiencing pain when I return to sport",
        "3. I am afraid of falling or losing my balance when I return to sport",
        "4. I am worried about my knee being able to withstand the demands of sport",
        "5. I feel relaxed about playing sport",
        "6. I feel confident that I can perform at my previous level of sport",
        "7. I feel confident about the strength of my knee",
        "8. I feel confident that my knee will not give way when playing sport",
        "9. Returning to sport is important to me",
        "10. I feel motivated to return to sport",
        "11. I feel that I will enjoy sport when I return",
        "12. I think that returning to sport is necessary for me to feel normal",
      ][i],
      type: "slider",
      min: 0,
      max: 10,
    })),
    calculate: (answers) => {
      const total = Array.from({ length: 12 }, (_, i) => Number(answers[`q${i + 1}`] || 0)).reduce((a, b) => a + b, 0);
      const score = Math.round((total / 120) * 100);
      return { score, max: 100 };
    },
    interpret: (score) => {
      if (score >= 77) return { label: "Psychologically Ready for RTS", color: "#34d399" };
      if (score >= 56) return { label: "Borderline — Address psychological barriers", color: "#fbbf24" };
      return { label: "NOT Ready for RTS — Psychological intervention needed", color: "#f87171" };
    },
  },

  TAMPA: {
    name: "Tampa Scale of Kinesiophobia (TSK-11)",
    shortName: "TSK-11",
    category: "Psychological",
    description: "11-item scale measuring fear of movement and re-injury. Score 11–44. Higher = more kinesiophobia. Predicts chronicity, disability, and poor treatment outcomes. Essential yellow flag screening.",
    mcid: "5 points",
    mdc: "N/A",
    timeToComplete: "4 min",
    items: 11,
    population: "Any MSK condition — chronic pain, post-injury fear of movement",
    reference: "Woby SR et al. Man Ther. 2005;10(3):197–201. DOI: 10.1016/j.math.2004.11.008",
    maxScore: 44,
    questions: [
      { id: "q1", label: "1. I'm afraid that I might injure myself if I exercise", type: "radio", options: ["Strongly disagree (1)", "Disagree (2)", "Agree (3)", "Strongly agree (4)"] },
      { id: "q2", label: "2. If I were to try to overcome it, my pain would increase", type: "radio", options: ["Strongly disagree (1)", "Disagree (2)", "Agree (3)", "Strongly agree (4)"] },
      { id: "q3", label: "3. My body is telling me I have something dangerously wrong", type: "radio", options: ["Strongly disagree (1)", "Disagree (2)", "Agree (3)", "Strongly agree (4)"] },
      { id: "q4", label: "4. My pain would probably be relieved if I were to exercise", type: "radio", options: ["Strongly disagree (4)", "Disagree (3)", "Agree (2)", "Strongly agree (1)"] },
      { id: "q5", label: "5. People aren't taking my medical condition seriously enough", type: "radio", options: ["Strongly disagree (1)", "Disagree (2)", "Agree (3)", "Strongly agree (4)"] },
      { id: "q6", label: "6. My accident has put my body at risk for the rest of my life", type: "radio", options: ["Strongly disagree (1)", "Disagree (2)", "Agree (3)", "Strongly agree (4)"] },
      { id: "q7", label: "7. Pain always means I have injured my body", type: "radio", options: ["Strongly disagree (1)", "Disagree (2)", "Agree (3)", "Strongly agree (4)"] },
      { id: "q8", label: "8. Just because something aggravates my pain doesn't mean it is dangerous", type: "radio", options: ["Strongly disagree (4)", "Disagree (3)", "Agree (2)", "Strongly agree (1)"] },
      { id: "q9", label: "9. I am afraid that I might accidentally do something that will cause injury", type: "radio", options: ["Strongly disagree (1)", "Disagree (2)", "Agree (3)", "Strongly agree (4)"] },
      { id: "q10", label: "10. Simply being careful that I do not make any unnecessary movements is the safest thing to do", type: "radio", options: ["Strongly disagree (1)", "Disagree (2)", "Agree (3)", "Strongly agree (4)"] },
      { id: "q11", label: "11. I wouldn't have this much pain if there wasn't something potentially dangerous going on in my body", type: "radio", options: ["Strongly disagree (1)", "Disagree (2)", "Agree (3)", "Strongly agree (4)"] },
    ],
    calculate: (answers) => {
      const total = ["q1","q2","q3","q4","q5","q6","q7","q8","q9","q10","q11"].reduce((s, id) => s + Number(answers[id] || 0), 0);
      return { score: total, max: 44 };
    },
    interpret: (score) => {
      if (score <= 27) return { label: "Low Kinesiophobia", color: "#34d399" };
      if (score <= 37) return { label: "Moderate Kinesiophobia — Address in treatment", color: "#fbbf24" };
      return { label: "High Kinesiophobia — Graded exposure needed", color: "#f87171" };
    },
  },

  OREBRO: {
    name: "Örebro Musculoskeletal Pain Screening Questionnaire",
    shortName: "ÖMPSQ",
    category: "Psychological",
    description: "21-item screening questionnaire identifying workers at risk of long-term pain and disability. Score 1–210. Higher = greater risk of chronicity. Predicts work disability at 6 months with 80% accuracy.",
    mcid: "N/A — screening tool",
    mdc: "N/A",
    timeToComplete: "10 min",
    items: 10,
    population: "Work-related MSK pain — occupational physiotherapy, LBP",
    reference: "Linton SJ, Hallden LM. Clin J Pain. 1998;14(2):124–130. DOI: 10.1097/00002508-199806000-00007",
    maxScore: 100,
    questions: [
      { id: "q1", label: "1. How long have you had your current pain problem?", type: "radio", options: ["0–1 week (10)", "1–2 weeks (8)", "3–4 weeks (6)", "4–8 weeks (4)", "3–6 months (2)", ">6 months (0)"] },
      { id: "q2", label: "2. How would you rate the pain you have had during the past week? (0=no pain, 10=worst pain)", type: "slider", min: 0, max: 10 },
      { id: "q3", label: "3. In the past 3 months, on average, how many days per week did you have pain?", type: "radio", options: ["0 days (10)", "1 day (8)", "2 days (6)", "3–4 days (4)", "5–7 days (0)"] },
      { id: "q4", label: "4. Based on all things you do to cope, how much are you able to reduce your pain?", type: "radio", options: ["Can reduce completely (10)", "Can reduce a great deal (8)", "Can reduce somewhat (6)", "Can reduce slightly (4)", "Cannot reduce at all (0)"] },
      { id: "q5", label: "5. How tense or anxious have you felt in the past week?", type: "radio", options: ["Not at all (10)", "Slightly (8)", "Moderately (6)", "Considerably (4)", "Extremely (0)"] },
      { id: "q6", label: "6. How much have you been bothered by feeling depressed in the past week?", type: "radio", options: ["Not at all (10)", "Slightly (8)", "Moderately (6)", "Considerably (4)", "Extremely (0)"] },
      { id: "q7", label: "7. In your view, how large is the risk that your current pain may become persistent?", type: "radio", options: ["No risk (10)", "Low risk (8)", "Moderate risk (6)", "High risk (4)", "Very high risk (0)"] },
      { id: "q8", label: "8. How much do you think physical activity will help reduce your pain?", type: "radio", options: ["It will help a lot (10)", "It will help (8)", "Somewhat (6)", "It will not help much (4)", "Activity makes it worse (0)"] },
      { id: "q9", label: "9. How worried are you that your pain might become worse if you go to work?", type: "radio", options: ["Not at all worried (10)", "Slightly worried (8)", "Moderately worried (6)", "Considerably worried (4)", "Extremely worried (0)"] },
      { id: "q10", label: "10. Do you think that you will be working in 6 months?", type: "radio", options: ["Definitely yes (10)", "Probably yes (8)", "Unsure (6)", "Probably not (4)", "Definitely not (0)"] },
    ],
    calculate: (answers) => {
      const total = ["q1","q2","q3","q4","q5","q6","q7","q8","q9","q10"].reduce((s, id) => {
        if (id === "q2") return s + (10 - Number(answers[id] || 0));
        return s + Number(answers[id] || 0);
      }, 0);
      return { score: total, max: 100 };
    },
    interpret: (score) => {
      if (score >= 70) return { label: "Low Risk of Chronicity", color: "#34d399" };
      if (score >= 50) return { label: "Medium Risk — Early intervention", color: "#fbbf24" };
      return { label: "High Risk of Chronicity — Multidisciplinary approach", color: "#f87171" };
    },
  },
};

// ══════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════

export default function OutcomeMeasures({ patient, session, notify }) {
  const [activeScale, setActiveScale] = useState(null);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [view, setView] = useState("list");
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (patient?.id) fetchHistory();
  }, [patient]);

  async function fetchHistory() {
    setLoadingHistory(true);
    const { data, error } = await supabase
      .from("outcome_measures")
      .select("*")
      .eq("patient_id", patient.id)
      .eq("user_id", session.user.id)
      .order("created_at", { ascending: false });
    if (!error) setHistory(data || []);
    setLoadingHistory(false);
  }

  function startScale(key) {
    setActiveScale(key);
    setAnswers({});
    setView("form");
  }

  function setAnswer(id, val) {
    setAnswers((prev) => ({ ...prev, [id]: val }));
  }

  async function submitScale() {
    if (!activeScale) return;
    const scale = SCALES[activeScale];
    const { score, max } = scale.calculate(answers);
    if (score === null) {
      notify("Please answer at least 27 questions for DASH scoring.", "error");
      return;
    }
    const interpretation = scale.interpret(score);
    setSaving(true);
    const { error } = await supabase.from("outcome_measures").insert([{
      patient_id: patient.id,
      user_id: session.user.id,
      scale_name: activeScale,
      score,
      max_score: max,
      interpretation: interpretation.label,
      answers,
    }]);
    if (error) { notify("Save failed: " + error.message, "error"); setSaving(false); return; }
    notify(`${scale.name} saved ✓`);
    setSaving(false);
    setView("list");
    setActiveScale(null);
    setAnswers({});
    fetchHistory();
  }

  // Filter scales
  const filteredScales = Object.entries(SCALES).filter(([key, scale]) => {
    const matchCat = activeCategory === "All" || scale.category === activeCategory;
    const matchSearch = !search ||
      scale.name.toLowerCase().includes(search.toLowerCase()) ||
      scale.population.toLowerCase().includes(search.toLowerCase()) ||
      scale.category.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const categories = ["All", ...Object.keys(SCALE_CATEGORIES)];

  // ── LIST VIEW ──
  if (view === "list") {
    return (
      <div>
        {/* Stats Banner */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 20 }}>
          {[
            { label: "Total Scales", value: Object.keys(SCALES).length, color: T.gold },
            { label: "Assessments Done", value: history.length, color: T.activeText },
            { label: "Scales Used", value: new Set(history.map(h => h.scale_name)).size, color: "#a78bfa" },
            { label: "Last Assessment", value: history[0] ? new Date(history[0].created_at).toLocaleDateString("en-IN") : "None", color: "#fb923c" },
          ].map((stat) => (
            <div key={stat.label} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 16px", textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: stat.color, fontFamily: "'Cormorant Garamond', serif" }}>{stat.value}</div>
              <div style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.6px" }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Search */}
        <input
          placeholder="🔍 Search scales, conditions, body regions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: "100%", marginBottom: 14, boxSizing: "border-box" }}
        />

        {/* Category Filter */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: "6px 14px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                cursor: "pointer", border: "none",
                background: activeCategory === cat ? T.gold : T.panel,
                color: activeCategory === cat ? "#07090e" : T.textMid,
                transition: "all 0.15s",
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Scale Cards Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 24 }}>
          {filteredScales.map(([key, scale]) => {
            const scaleHistory = history.filter((h) => h.scale_name === key);
            const lastEntry = scaleHistory[0];
            const prevEntry = scaleHistory[1];
            const change = lastEntry && prevEntry ? lastEntry.score - prevEntry.score : null;
            const interp = lastEntry ? scale.interpret(lastEntry.score) : null;
            return (
              <div
                key={key}
                className="card row-hover"
                style={{ padding: 20, cursor: "pointer", borderTop: `2px solid ${T.gold}`, display: "flex", flexDirection: "column" }}
                onClick={() => startScale(key)}
              >
                {/* Header */}
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.goldLight, marginBottom: 3, lineHeight: 1.4 }}>{scale.name}</div>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: "rgba(201,169,110,0.1)", color: T.gold }}>
                      {scale.category}
                    </span>
                  </div>
                </div>

                {/* Description */}
                <div style={{ fontSize: 11, color: T.textDim, lineHeight: 1.6, marginBottom: 10 }}>{scale.description}</div>

                {/* Meta info */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 12 }}>
                  {[
                    { label: "Items", value: scale.items },
                    { label: "Time", value: scale.timeToComplete },
                    { label: "MCID", value: scale.mcid },
                    { label: "Population", value: scale.population.split("—")[0] },
                  ].map((meta) => (
                    <div key={meta.label} style={{ background: T.panel, borderRadius: 6, padding: "6px 8px" }}>
                      <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.4px" }}>{meta.label}</div>
                      <div style={{ fontSize: 10, color: T.textMid, fontWeight: 600, marginTop: 1 }}>{meta.value}</div>
                    </div>
                  ))}
                </div>

                {/* Last Score */}
                {lastEntry ? (
                  <div style={{ background: T.panel, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: T.textDim, marginBottom: 6 }}>Last Assessment</div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 700, color: T.gold }}>
                        {lastEntry.score}<span style={{ fontSize: 12, color: T.textDim }}>/{lastEntry.max_score}</span>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 12, background: "rgba(0,0,0,0.3)", color: interp?.color }}>
                          {lastEntry.interpretation}
                        </span>
                        {change !== null && (
                          <div style={{ fontSize: 10, marginTop: 4, color: change > 0 ? T.activeText : "#f87171", fontWeight: 700 }}>
                            {change > 0 ? "▲" : "▼"} {Math.abs(change)} pts {change > 0 ? "improved" : "worsened"}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div style={{ height: 3, borderRadius: 2, background: T.border, marginTop: 8, overflow: "hidden" }}>
                      <div style={{ height: "100%", borderRadius: 2, background: interp?.color, width: `${(lastEntry.score / lastEntry.max_score) * 100}%`, transition: "width 0.3s" }} />
                    </div>
                    <div style={{ fontSize: 9, color: T.textDim, marginTop: 4 }}>
                      {new Date(lastEntry.created_at).toLocaleDateString("en-IN")} · MCID: {scale.mcid}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: T.textDim, fontStyle: "italic", marginBottom: 10, flex: 1 }}>Not yet assessed</div>
                )}

                {/* Reference */}
                <div style={{ fontSize: 9, color: T.textDim, lineHeight: 1.5, marginBottom: 10, borderTop: `1px solid ${T.border}`, paddingTop: 8 }}>
                  📚 {scale.reference}
                </div>

                <button
                  className="btn btn-gold btn-sm"
                  style={{ width: "100%", marginTop: "auto" }}
                  onClick={(e) => { e.stopPropagation(); startScale(key); }}
                >
                  + Assess Now
                </button>
              </div>
            );
          })}
        </div>

        {/* Assessment History */}
        {history.length > 0 && (
          <div className="card" style={{ padding: 24 }}>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, fontWeight: 600, color: T.goldLight, marginBottom: 16 }}>
              Assessment History ({history.length} total)
            </div>
            {loadingHistory ? (
              <div style={{ color: T.textDim, fontSize: 13 }}>Loading...</div>
            ) : (
              history.map((entry) => {
                const scale = SCALES[entry.scale_name];
                const interp = scale?.interpret(entry.score);
                return (
                  <div key={entry.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: `1px solid ${T.border}` }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{scale?.name || entry.scale_name}</div>
                      <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>
                        {new Date(entry.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        {scale && <span style={{ color: T.gold, marginLeft: 8 }}>MCID: {scale.mcid}</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 700, color: T.gold }}>
                        {entry.score}<span style={{ fontSize: 12, color: T.textDim }}>/{entry.max_score}</span>
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: "rgba(0,0,0,0.3)", color: interp?.color }}>
                        {entry.interpretation}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    );
  }

  // ── FORM VIEW ──
  if (view === "form" && activeScale) {
    const scale = SCALES[activeScale];
    const { score, max } = scale.calculate(answers);
    const interpretation = score !== null ? scale.interpret(score) : null;
    const answeredCount = Object.keys(answers).length;
    const totalQ = scale.questions.length;

    return (
      <div style={{ overflowX: "hidden" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <button className="btn btn-outline btn-sm" onClick={() => { setView("list"); setActiveScale(null); setAnswers({}); }}>← Back</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, fontWeight: 600, color: T.goldLight }}>{scale.name}</div>
            <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>
              {answeredCount}/{totalQ} answered · MCID: {scale.mcid} · {scale.timeToComplete}
            </div>
          </div>
          {score !== null && interpretation && (
            <div style={{ background: T.panel, borderRadius: 10, padding: "10px 16px", textAlign: "center", border: `1px solid ${interpretation.color}`, flexShrink: 0 }}>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, fontWeight: 700, color: interpretation.color }}>
                {score}<span style={{ fontSize: 14, color: T.textDim }}>/{max}</span>
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: interpretation.color }}>{interpretation.label}</div>
            </div>
          )}
        </div>

        {/* Reference */}
        <div style={{ background: "rgba(201,169,110,0.04)", border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px", marginBottom: 16, fontSize: 10, color: T.textDim }}>
          📚 {scale.reference} · Population: {scale.population}
        </div>

        {/* Progress bar */}
        <div style={{ height: 4, borderRadius: 2, background: T.border, marginBottom: 20, overflow: "hidden" }}>
          <div style={{ height: "100%", borderRadius: 2, background: `linear-gradient(90deg,${T.gold},${T.goldLight})`, width: `${(answeredCount / totalQ) * 100}%`, transition: "width 0.3s" }} />
        </div>

        {/* Questions */}
        <div style={{ display: "grid", gap: 14, marginBottom: 100 }}>
          {scale.questions.map((q) => (
            <div key={q.id} className="card" style={{ padding: 16, borderLeft: answers[q.id] !== undefined ? `3px solid ${T.gold}` : `3px solid ${T.border}` }}>
              <FieldLabel>{q.label}</FieldLabel>
              {q.type === "text" ? (
                <input
                  type="text"
                  placeholder="Describe the activity..."
                  value={answers[q.id] || ""}
                  onChange={(e) => setAnswer(q.id, e.target.value)}
                  style={{ width: "100%", boxSizing: "border-box" }}
                />
              ) : q.type === "slider" ? (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.textDim, marginBottom: 8 }}>
                    <span>{q.min}</span><span>{q.max}</span>
                  </div>
                  <input
                    type="range" min={q.min} max={q.max} step={1}
                    value={answers[q.id] ?? Math.round((q.min + q.max) / 2)}
                    onChange={(e) => setAnswer(q.id, Number(e.target.value))}
                    style={{ width: "100%", accentColor: T.gold }}
                  />
                  <div style={{ textAlign: "center", marginTop: 6 }}>
                    <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, fontWeight: 700, color: q.id.includes("pain") || q.id === "vas" || q.id === "current" || q.id === "worst" || q.id === "best" ? getPainColor(answers[q.id] ?? 5) : T.gold }}>
                      {answers[q.id] ?? Math.round((q.min + q.max) / 2)}
                    </span>
                    <span style={{ fontSize: 12, color: T.textDim }}> / {q.max}</span>
                  </div>
                </div>
              ) : (
                <div style={{ display: "grid", gap: 6 }}>
                  {q.options.map((opt, idx) => (
                    <div
                      key={idx}
                      onClick={() => setAnswer(q.id, idx)}
                      style={{
                        display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                        borderRadius: 7, cursor: "pointer",
                        background: answers[q.id] === idx ? "rgba(201,169,110,0.1)" : "transparent",
                        border: `1px solid ${answers[q.id] === idx ? T.gold : T.border}`,
                        transition: "all 0.15s",
                      }}
                    >
                      <div style={{
                        width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                        border: `2px solid ${answers[q.id] === idx ? T.gold : T.textDim}`,
                        background: answers[q.id] === idx ? T.gold : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {answers[q.id] === idx && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#07090e" }} />}
                      </div>
                      <span style={{ fontSize: 12, color: answers[q.id] === idx ? T.goldLight : T.textMid, lineHeight: 1.5, flex: 1 }}>
                        {opt}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Sticky Save */}
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 24px", background: T.panel, borderTop: `1px solid ${T.border}`, display: "flex", gap: 12, zIndex: 50 }}>
          <button
            className="btn btn-gold"
            style={{ flex: 1, padding: 14 }}
            onClick={submitScale}
            disabled={saving || answeredCount === 0}
          >
            {saving ? "Saving..." : `Save Assessment (${answeredCount}/${totalQ} answered)`}
          </button>
          <button className="btn btn-outline" onClick={() => { setView("list"); setActiveScale(null); setAnswers({}); }}>Cancel</button>
        </div>
      </div>
    );
  }

  return null;
}